// @ts-nocheck
/**
 * Data Management Dashboard
 * D1 Schema visualization and management interface
 */

import React, { useState, useEffect } from 'react';
import {
  DataTable,
  StructuredList,
  StructuredListWrapper,
  StructuredListHead,
  StructuredListBody,
  StructuredListRow,
  StructuredListCell,
  InlineLoading,
  SkeletonText,
  Button,
  Tag,
  Tabs,
  Tab,
  TabList,
  TabPanels,
  TabPanel,
  Modal,
  CodeSnippet,
  InlineNotification,
  Breadcrumb,
  BreadcrumbItem,
  Grid,
  Column,
  Tile,
  Search,
} from '@carbon/react';
import { Data_1, DataBase, Table, UserData, View, Security, Analytics, ChevronRight } from '@carbon/icons-react';
import { useAuthStore } from '../../store/authStore';
import axios from 'axios';

interface Schema {
  table_name: string;
  columns: Array<{
    name: string;
    type: string;
    nullable: boolean;
    default_value: any;
    is_primary_key: boolean;
  }>;
  indexes: Array<{
    index_name: string;
    index_statement: string;
  }>;
  row_count: number;
  create_statement: string;
}

interface AnalysisSuggestion {
  type: string;
  severity: 'low' | 'medium' | 'high';
  message: string;
  suggested_sql?: string;
}

// API base URL
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

const DataManagement: React.FC = () => {
  const { user } = useAuthStore();
  const [schemas, setSchemas] = useState<Schema[]>([]);
  const [selectedTable, setSelectedTable] = useState<Schema | null>(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResults, setAnalysisResults] = useState<any>(null);
  const [showSqlModal, setShowSqlModal] = useState(false);
  const [currentSql, setCurrentSql] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user?.role === 'platform_admin') {
      loadSchemas();
    } else {
      setError('Insufficient permissions to access data management');
      setLoading(false);
    }
  }, [user]);

  const loadSchemas = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
      const response = await axios.get(`${API_BASE_URL}/api/v1/schemas`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.data.success) {
        setSchemas(response.data.data.schemas);
        if (response.data.data.schemas.length > 0) {
          setSelectedTable(response.data.data.schemas[0]);
        }
      }
    } catch (err) {
      console.error('Error loading schemas:', err);
      setError('Failed to load database schemas');
    } finally {
      setLoading(false);
    }
  };

  const analyzeSchemas = async () => {
    try {
      setAnalyzing(true);
      const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
      const response = await axios.post(
        `${API_BASE_URL}/api/v1/schemas/analyze`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.data.success) {
        setAnalysisResults(response.data.data);
      }
    } catch (err) {
      console.error('Error analyzing schemas:', err);
      setError('Failed to analyze schemas');
    } finally {
      setAnalyzing(false);
    }
  };

  const showCreateStatement = (sql: string) => {
    setCurrentSql(sql);
    setShowSqlModal(true);
  };

  const filteredSchemas = schemas.filter(schema => schema.table_name.toLowerCase().includes(searchTerm.toLowerCase()));

  if (error) {
    return (
      <div className="p-4">
        <InlineNotification kind="error" title="Access Error" subtitle={error} hideCloseButton />
      </div>
    );
  }

  const tableHeaders = [
    { key: 'table_name', header: 'Table Name' },
    { key: 'row_count', header: 'Row Count' },
    { key: 'column_count', header: 'Columns' },
    { key: 'index_count', header: 'Indexes' },
    { key: 'actions', header: 'Actions' },
  ];

  const tableRows = filteredSchemas.map(schema => ({
    id: schema.table_name,
    table_name: (
      <div className="flex items-center gap-2">
        <Table size={16} />
        {schema.table_name}
      </div>
    ),
    row_count: schema.row_count.toLocaleString(),
    column_count: schema.columns.length,
    index_count: schema.indexes.length,
    actions: (
      <Button kind="ghost" size="sm" renderIcon={ChevronRight} onClick={() => setSelectedTable(schema)}>
        View Details
      </Button>
    ),
  }));

  return (
    <div className="min-h-screen bg-gray-10">
      <div className="p-6">
        <Breadcrumb noTrailingSlash>
          <BreadcrumbItem href="/admin">Admin</BreadcrumbItem>
          <BreadcrumbItem isCurrentPage>Data Management</BreadcrumbItem>
        </Breadcrumb>

        <div className="mt-6 mb-8">
          <h1 className="text-3xl font-semibold flex items-center gap-3">
            <DataBase size={32} />
            D1 Database Schema Management
          </h1>
          <p className="text-gray-60 mt-2">Inspect and manage database schemas, relationships, and compliance</p>
        </div>

        {loading ? (
          <Grid>
            <Column lg={16}>
              <SkeletonText paragraph lineCount={5} />
            </Column>
          </Grid>
        ) : (
          <Tabs>
            <TabList aria-label="Schema management tabs">
              <Tab>Schema Overview</Tab>
              <Tab>Table Details</Tab>
              <Tab>Performance Analysis</Tab>
              <Tab>Compliance Audit</Tab>
            </TabList>

            <TabPanels>
              {/* Schema Overview Tab */}
              <TabPanel>
                <Grid className="mt-6">
                  <Column lg={4} md={4} sm={4}>
                    <Tile className="h-full">
                      <div className="flex items-center justify-between mb-2">
                        <Data_1 size={24} className="text-blue-60" />
                        <Tag type="blue" size="sm">
                          Active
                        </Tag>
                      </div>
                      <h3 className="text-2xl font-bold">{schemas.length}</h3>
                      <p className="text-gray-60">Total Tables</p>
                    </Tile>
                  </Column>

                  <Column lg={4} md={4} sm={4}>
                    <Tile className="h-full">
                      <div className="flex items-center justify-between mb-2">
                        <UserData size={24} className="text-green-60" />
                        <Tag type="green" size="sm">
                          Indexed
                        </Tag>
                      </div>
                      <h3 className="text-2xl font-bold">{schemas.reduce((acc, s) => acc + s.indexes.length, 0)}</h3>
                      <p className="text-gray-60">Total Indexes</p>
                    </Tile>
                  </Column>

                  <Column lg={4} md={4} sm={4}>
                    <Tile className="h-full">
                      <div className="flex items-center justify-between mb-2">
                        <Analytics size={24} className="text-purple-60" />
                        <Tag type="purple" size="sm">
                          Optimized
                        </Tag>
                      </div>
                      <h3 className="text-2xl font-bold">
                        {schemas.reduce((acc, s) => acc + s.row_count, 0).toLocaleString()}
                      </h3>
                      <p className="text-gray-60">Total Records</p>
                    </Tile>
                  </Column>

                  <Column lg={4} md={4} sm={4}>
                    <Tile className="h-full">
                      <div className="flex items-center justify-between mb-2">
                        <Security size={24} className="text-red-60" />
                        <Tag type="red" size="sm">
                          Encrypted
                        </Tag>
                      </div>
                      <h3 className="text-2xl font-bold">100%</h3>
                      <p className="text-gray-60">PII Protection</p>
                    </Tile>
                  </Column>
                </Grid>

                <div className="mt-8">
                  <div className="flex justify-between items-center mb-4">
                    <Search
                      size="lg"
                      placeholder="Search tables..."
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                      className="max-w-md"
                    />
                    <Button kind="secondary" renderIcon={Analytics} onClick={analyzeSchemas} disabled={analyzing}>
                      {analyzing ? 'Analyzing...' : 'Analyze Performance'}
                    </Button>
                  </div>

                  <DataTable
                    rows={tableRows}
                    headers={tableHeaders}
                    render={({ rows, headers, getTableProps, getHeaderProps, getRowProps }) => (
                      <DataTable.TableContainer>
                        <DataTable.Table {...getTableProps()}>
                          <DataTable.TableHead>
                            <DataTable.TableRow>
                              {headers.map(header => (
                                <DataTable.TableHeader {...getHeaderProps({ header })}>
                                  {header.header}
                                </DataTable.TableHeader>
                              ))}
                            </DataTable.TableRow>
                          </DataTable.TableHead>
                          <DataTable.TableBody>
                            {rows.map(row => (
                              <DataTable.TableRow {...getRowProps({ row })}>
                                {row.cells.map(cell => (
                                  <DataTable.TableCell key={cell.id}>{cell.value}</DataTable.TableCell>
                                ))}
                              </DataTable.TableRow>
                            ))}
                          </DataTable.TableBody>
                        </DataTable.Table>
                      </DataTable.TableContainer>
                    )}
                  />
                </div>
              </TabPanel>

              {/* Table Details Tab */}
              <TabPanel>
                {selectedTable && (
                  <div className="mt-6">
                    <div className="mb-6">
                      <h2 className="text-xl font-semibold mb-2">{selectedTable.table_name}</h2>
                      <div className="flex gap-4">
                        <Tag type="blue">{selectedTable.row_count} rows</Tag>
                        <Tag type="green">{selectedTable.columns.length} columns</Tag>
                        <Tag type="purple">{selectedTable.indexes.length} indexes</Tag>
                      </div>
                    </div>

                    <Grid>
                      <Column lg={8}>
                        <h3 className="text-lg font-semibold mb-3">Columns</h3>
                        <StructuredListWrapper>
                          <StructuredListHead>
                            <StructuredListRow head>
                              <StructuredListCell head>Name</StructuredListCell>
                              <StructuredListCell head>Type</StructuredListCell>
                              <StructuredListCell head>Nullable</StructuredListCell>
                              <StructuredListCell head>Default</StructuredListCell>
                            </StructuredListRow>
                          </StructuredListHead>
                          <StructuredListBody>
                            {selectedTable.columns.map(col => (
                              <StructuredListRow key={col.name}>
                                <StructuredListCell>
                                  {col.name}
                                  {col.is_primary_key && (
                                    <Tag type="cyan" size="sm" className="ml-2">
                                      PK
                                    </Tag>
                                  )}
                                </StructuredListCell>
                                <StructuredListCell>{col.type}</StructuredListCell>
                                <StructuredListCell>
                                  <Tag type={col.nullable ? 'gray' : 'green'} size="sm">
                                    {col.nullable ? 'NULL' : 'NOT NULL'}
                                  </Tag>
                                </StructuredListCell>
                                <StructuredListCell>{col.default_value || '-'}</StructuredListCell>
                              </StructuredListRow>
                            ))}
                          </StructuredListBody>
                        </StructuredListWrapper>
                      </Column>

                      <Column lg={8}>
                        <h3 className="text-lg font-semibold mb-3">Indexes</h3>
                        {selectedTable.indexes.length > 0 ? (
                          <StructuredListWrapper>
                            <StructuredListHead>
                              <StructuredListRow head>
                                <StructuredListCell head>Index Name</StructuredListCell>
                                <StructuredListCell head>Actions</StructuredListCell>
                              </StructuredListRow>
                            </StructuredListHead>
                            <StructuredListBody>
                              {selectedTable.indexes.map(idx => (
                                <StructuredListRow key={idx.index_name}>
                                  <StructuredListCell>{idx.index_name}</StructuredListCell>
                                  <StructuredListCell>
                                    <Button
                                      kind="ghost"
                                      size="sm"
                                      onClick={() => showCreateStatement(idx.index_statement || '')}
                                    >
                                      View SQL
                                    </Button>
                                  </StructuredListCell>
                                </StructuredListRow>
                              ))}
                            </StructuredListBody>
                          </StructuredListWrapper>
                        ) : (
                          <Tile>
                            <p className="text-gray-60">No indexes defined for this table</p>
                          </Tile>
                        )}

                        <Button
                          kind="tertiary"
                          className="mt-4"
                          onClick={() => showCreateStatement(selectedTable.create_statement)}
                        >
                          View Create Statement
                        </Button>
                      </Column>
                    </Grid>
                  </div>
                )}
              </TabPanel>

              {/* Performance Analysis Tab */}
              <TabPanel>
                <div className="mt-6">
                  {analyzing ? (
                    <InlineLoading description="Analyzing schema performance..." />
                  ) : analysisResults ? (
                    <div>
                      <div className="mb-6">
                        <h2 className="text-xl font-semibold mb-2">Performance Analysis</h2>
                        <div className="flex gap-4">
                          <Tag type="blue">{analysisResults.total_tables} tables analyzed</Tag>
                          <Tag type={analysisResults.summary.tables_needing_attention > 0 ? 'red' : 'green'}>
                            {analysisResults.summary.tables_needing_attention} need attention
                          </Tag>
                          <Tag type="purple">{analysisResults.summary.total_suggestions} suggestions</Tag>
                        </div>
                      </div>

                      {analysisResults.analysis.map((table: any) => (
                        <Tile key={table.table_name} className="mb-4">
                          <h3 className="text-lg font-semibold mb-2">{table.table_name}</h3>
                          <div className="flex gap-4 mb-3">
                            <span className="text-sm text-gray-60">
                              {table.row_count} rows • {table.index_count} indexes • {table.foreign_key_count} FKs
                            </span>
                          </div>

                          {table.suggestions.length > 0 ? (
                            <div className="space-y-2">
                              {table.suggestions.map((suggestion: AnalysisSuggestion, idx: number) => (
                                <InlineNotification
                                  key={idx}
                                  kind={
                                    suggestion.severity === 'high'
                                      ? 'error'
                                      : suggestion.severity === 'medium'
                                        ? 'warning'
                                        : 'info'
                                  }
                                  title={suggestion.message}
                                  subtitle={suggestion.suggested_sql}
                                  hideCloseButton
                                />
                              ))}
                            </div>
                          ) : (
                            <Tag type="green">Optimized</Tag>
                          )}
                        </Tile>
                      ))}
                    </div>
                  ) : (
                    <Tile>
                      <p className="text-gray-60">
                        Click "Analyze Performance" to scan your database for optimization opportunities.
                      </p>
                    </Tile>
                  )}
                </div>
              </TabPanel>

              {/* Compliance Audit Tab */}
              <TabPanel>
                <div className="mt-6">
                  <h2 className="text-xl font-semibold mb-4">Compliance Status</h2>

                  <Grid>
                    <Column lg={8}>
                      <Tile className="mb-4">
                        <h3 className="text-lg font-semibold mb-3">FERPA Compliance</h3>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span>Educational Records Protection</span>
                            <Tag type="green" size="sm">
                              Compliant
                            </Tag>
                          </div>
                          <div className="flex justify-between">
                            <span>Parent Access Controls</span>
                            <Tag type="green" size="sm">
                              Enabled
                            </Tag>
                          </div>
                          <div className="flex justify-between">
                            <span>Data Retention Policy</span>
                            <Tag type="green" size="sm">
                              Configured
                            </Tag>
                          </div>
                          <div className="flex justify-between">
                            <span>Audit Logging</span>
                            <Tag type="green" size="sm">
                              Active
                            </Tag>
                          </div>
                        </div>
                      </Tile>
                    </Column>

                    <Column lg={8}>
                      <Tile className="mb-4">
                        <h3 className="text-lg font-semibold mb-3">COPPA Compliance</h3>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span>Parental Consent System</span>
                            <Tag type="green" size="sm">
                              Implemented
                            </Tag>
                          </div>
                          <div className="flex justify-between">
                            <span>Under-13 Data Protection</span>
                            <Tag type="green" size="sm">
                              Enforced
                            </Tag>
                          </div>
                          <div className="flex justify-between">
                            <span>Data Minimization</span>
                            <Tag type="green" size="sm">
                              Active
                            </Tag>
                          </div>
                          <div className="flex justify-between">
                            <span>Deletion Requests</span>
                            <Tag type="green" size="sm">
                              Automated
                            </Tag>
                          </div>
                        </div>
                      </Tile>
                    </Column>
                  </Grid>

                  <Tile>
                    <h3 className="text-lg font-semibold mb-3">Recent Compliance Events</h3>
                    <StructuredListWrapper>
                      <StructuredListHead>
                        <StructuredListRow head>
                          <StructuredListCell head>Event Type</StructuredListCell>
                          <StructuredListCell head>User</StructuredListCell>
                          <StructuredListCell head>Status</StructuredListCell>
                          <StructuredListCell head>Timestamp</StructuredListCell>
                        </StructuredListRow>
                      </StructuredListHead>
                      <StructuredListBody>
                        <StructuredListRow>
                          <StructuredListCell>Data Export Request</StructuredListCell>
                          <StructuredListCell>parent_user_123</StructuredListCell>
                          <StructuredListCell>
                            <Tag type="green" size="sm">
                              Completed
                            </Tag>
                          </StructuredListCell>
                          <StructuredListCell>{new Date().toLocaleString()}</StructuredListCell>
                        </StructuredListRow>
                        <StructuredListRow>
                          <StructuredListCell>Consent Granted</StructuredListCell>
                          <StructuredListCell>student_user_456</StructuredListCell>
                          <StructuredListCell>
                            <Tag type="green" size="sm">
                              Success
                            </Tag>
                          </StructuredListCell>
                          <StructuredListCell>{new Date().toLocaleString()}</StructuredListCell>
                        </StructuredListRow>
                      </StructuredListBody>
                    </StructuredListWrapper>
                  </Tile>
                </div>
              </TabPanel>
            </TabPanels>
          </Tabs>
        )}

        {/* SQL Modal */}
        <Modal
          open={showSqlModal}
          onRequestClose={() => setShowSqlModal(false)}
          modalHeading="SQL Statement"
          primaryButtonText="Close"
          onRequestSubmit={() => setShowSqlModal(false)}
        >
          <CodeSnippet type="multi" feedback="Copied to clipboard">
            {currentSql}
          </CodeSnippet>
        </Modal>
      </div>
    </div>
  );
};

export default DataManagement;
